<div class="menu-btn" (click)="sidebarToggle();" [class.active]="sidebarVisible" role="button" tabindex="0"
  (keydown.enter)="sidebarToggle()" (keydown.space)="sidebarToggle(); $event.preventDefault()"
  [attr.aria-label]="sidebarVisible ? ('sidebar.collapse' | translate) : ('sidebar.expand' | translate)">
  <mat-icon>chevron_left</mat-icon>
</div>
<!-- Mobile backdrop -->
<div class="sidebar-backdrop" [class.active]="sidebarVisible && (isMobile || isTablet)" (click)="sidebarToggle()"></div>
<div class="sidebar" [class.active]="sidebarVisible">
  <div class="sidebar-container">
    <!-- Standalone logo: only for the pure-admin nav (no connected client).
         The client nav below renders its own compact logo + identity header
         instead, so the two never both appear. -->
    @if (!isConnectClient()) {
    <div class="logo">
      @if (!isAdminImpersonating() && (selectedSite?.logo?.url || selectedSite?.logo?.path)) {
      <img [src]="cdnUrl + '/' + (selectedSite.logo.url || selectedSite.logo.path)" [alt]="selectedSite.name" />
      } @else {
      <img src="assets/img/logo.png" alt="Logo" />
      }
    </div>
    }

    <!-- Site Selector: hidden while only one site exists — a single-option
         switcher implies multi-site support that isn't real yet. Re-enables
         itself automatically once a client has more than one active site. -->
    @if (sites.length > 1) {
    <div class="site-selector-container">
      <div class="site-select-btn" [matMenuTriggerFor]="siteMenu">
        <div class="site-content">
          <div class="site-icon-badge">
            <mat-icon>storefront</mat-icon>
          </div>
          <span [title]="selectedSite?.name">{{ selectedSite?.name || 'Select Site' }}</span>
        </div>
        <mat-icon class="arrow-icon">arrow_drop_down</mat-icon>
      </div>
      <mat-menu #siteMenu="matMenu" class="premium-site-menu" panelClass="premium-site-menu-panel">
        @for (site of sites; track site.id) {
        <button mat-menu-item (click)="onSiteChange(site)" [class.active-site]="site.id === selectedSite?.id">
          <mat-icon class="site-icon">storefront</mat-icon>
          <span class="site-name">{{ site.name }}</span>
          @if (site.id === selectedSite?.id) {
          <mat-icon class="selected-check">check</mat-icon>
          }
        </button>
        }
      </mat-menu>
    </div>
    }

    <!--************* ADMIN *************-->
    <!-- Pure admin only. Mutually exclusive with the CLIENT block below by
         construction: both used to be independent @if branches keyed off
         different booleans (this block on role alone, the client block on
         isConnectClient()), so an ADMIN impersonating a client rendered
         BOTH — the full platform nav stacked above a full merchant nav.
         Excluding isConnectClient() here — the exact same condition the
         CLIENT block already renders on — guarantees exactly one of the
         two blocks renders per session, not two independently-evaluated
         conditions that can (and did) disagree. -->
    @if (user?._role === 'ADMIN' && !isConnectClient()) {
    <div class="sidebar-nav">

      <!-- Primary -->
      <a [routerLinkActive]="['active']"
        [routerLink]="isConnectClient() && client ? ['/admin/clients', client.uniq] : ['/admin/clients']" class="item">
        <mat-icon>store</mat-icon> {{ 'sidebar.clients_menu' | translate }}
        @if (badges?.clients) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.clients}}</strong>
        </span>
        }
      </a>
      @if (isUs()) {
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/orders']" class="item">
        <mat-icon>shopping_cart</mat-icon> {{ 'sidebar.orders_menu' | translate }}
        <span class="flex ml-auto">
          @if (badges?.admin_orders_alert) {
          <strong class="ui red circular label">{{badges?.admin_orders_alert}}</strong>
          }
          @if (badges?.admin_orders) {
          <strong class="ui circular label ml-2">{{badges?.admin_orders}}</strong>
          }
        </span>
      </a>
      }

      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.drivers_menu' | translate }}</div>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/drivers']" class="item">
        <mat-icon>local_shipping</mat-icon> {{ 'sidebar.drivers_menu' | translate }}
        @if (badges?.drivers) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.drivers}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/payouts']" class="item">
        <mat-icon>payments</mat-icon> {{ 'sidebar.payouts_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/route-optimization']" class="item">
        <mat-icon>route</mat-icon> {{ 'sidebar.route_optimization_menu' | translate }}
      </a>

      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.growth_section' | translate }}</div>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/leads']" class="item">
        <mat-icon>contact_mail</mat-icon> {{ 'sidebar.leads_menu' | translate }}
        @if (badges?.leads) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.leads}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/support']" class="item">
        <mat-icon>support_agent</mat-icon> {{ 'sidebar.support_menu' | translate }}
        <span class="flex ml-auto">
          @if (badges?.support_escalated) {
          <strong class="ui red circular label">{{badges?.support_escalated}}</strong>
          }
          @if (badges?.support_open) {
          <strong class="ui circular label ml-2">{{badges?.support_open}}</strong>
          }
        </span>
      </a>

      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.platform_section' | translate }}</div>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/accounting']" class="item">
        <mat-icon>point_of_sale</mat-icon> {{ 'sidebar.accounting_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/global-categories']" class="item">
        <mat-icon>category</mat-icon> {{ 'sidebar.global_categories_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/vocations']" class="item">
        <mat-icon>storefront</mat-icon> {{ 'sidebar.vocations_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/products']" class="item">
        <mat-icon>inventory_2</mat-icon> {{ 'sidebar.master_products_menu' | translate }}
        @if (badges?.master_products) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.master_products}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/banner']" class="item">
        <mat-icon>image</mat-icon> {{ 'sidebar.banners_menu' | translate }}
        @if (badges?.slides) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.slides}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/loyalty']" class="item">
        <mat-icon>stars</mat-icon> {{ 'sidebar.loyalty_menu' | translate }}
      </a>
      @if (isUs()) {
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/reviews']" class="item">
        <mat-icon>rate_review</mat-icon> {{ 'sidebar.reviews_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/admin/infos']" class="item">
        <mat-icon>cloud_download</mat-icon> {{ 'sidebar.download_push_menu' | translate }}
      </a>
      }

      <div class="divider"></div>
      <a [routerLinkActive]="['active']" [routerLink]="['/activity-log']" class="item">
        <mat-icon>history</mat-icon> {{ 'sidebar.activity_log_menu' | translate }}
      </a>

      <div class="divider"></div>
      <a (click)="logout()" class="item logout-item" role="button" tabindex="0" (keydown.enter)="logout()"
        (keydown.space)="logout(); $event.preventDefault()">
        <mat-icon>exit_to_app</mat-icon> {{ 'sidebar.logout_menu' | translate }}
      </a>
      <div class="divider"></div>
    </div>
    }

    <!--************* CLIENT *************-->
    @if (isConnectClient()) {
    <div class="sidebar-nav">

      <!-- Compact merchant identity: logo + name + CLIENT MODE/ADMIN MODE +
           lock, merged into a single header (was a large standalone logo
           plus a separate mode card). -->
      <div class="merchant-header" [class.admin-client-header]="client && user?._role === 'ADMIN'"
        [class.client-header]="client && user?._role !== 'ADMIN'">
        <div class="logo">
          @if (client && user?._role === 'ADMIN') {
          @if (clientLogoUrl) {
          <img [src]="clientLogoUrl" [alt]="client.company" class="client-logo-img" />
          } @else {
          <mat-icon>store</mat-icon>
          }
          } @else {
          @if (!isAdminImpersonating() && (selectedSite?.logo?.url || selectedSite?.logo?.path)) {
          <img [src]="cdnUrl + '/' + (selectedSite.logo.url || selectedSite.logo.path)" [alt]="selectedSite?.name" />
          } @else {
          <img src="assets/img/logo.png" alt="Logo" />
          }
          }
        </div>
        @if (client) {
        <div class="client-details">
          <span class="client-name" [title]="client.company">{{client.company}}</span>
          <span class="client-role">{{ (user?._role === 'ADMIN' ? 'sidebar.admin_mode_menu' : 'sidebar.client_mode_menu') | translate }}</span>
        </div>
        @if (user?._role === 'ADMIN') {
        <button mat-icon-button (click)="adminDisconectClient()" class="disconnect-btn"
          [title]="'sidebar.logout_menu' | translate" matRipple>
          <mat-icon>lock_open</mat-icon>
        </button>
        } @else {
        <button mat-icon-button [routerLink]="['/login']" class="disconnect-btn"
          [title]="'sidebar.logout_menu' | translate" matRipple>
          <mat-icon>lock</mat-icon>
        </button>
        }
        }
      </div>

      <!-- Admin-as-client detail row -->
      @if (client && user?._role === 'ADMIN') {
      <div class="item-client-infos">
        <span class="item item-inline">
          <mat-icon>person</mat-icon>{{client.manager}}
          <div class="space-client-info"></div>
          <mat-icon>vpn_key</mat-icon>#{{clientKey}}
        </span>
        <span class="item" style="display: block">
          <mat-icon>location_on</mat-icon>{{client.address + ' '}}{{client.postcode + ' '}}{{client.city}}
        </span>
        @if (client.email) {
        <span class="item">
          <mat-icon>email</mat-icon>{{client.email}}
        </span>
        }
        @if (client.phone) {
        <span class="item">
          <mat-icon>call</mat-icon>{{client.phone}}
        </span>
        }
        @if (service_url) {
        <span class="item" style="display: block" title="{{service_url}}">
          <mat-icon>public</mat-icon><a href="{{ 'https://' }}{{service_url}}" target="_blank">{{
            'sidebar.go_to_site_menu' | translate }}</a>
        </span>
        }
      </div>
      }

      <!-- Settings: a normal nav item, same pattern as Dashboard/Analytics
           (was an oversized standalone action button). -->
      <a [routerLinkActive]="['active']" [routerLink]="['/settings']" class="item">
        <mat-icon>settings</mat-icon> {{ 'sidebar.settings_menu' | translate }}
      </a>

      <!-- Admin import shortcut -->
      @if (user?._role === 'ADMIN') {
      <a [routerLink]="['/admin/import']" class="item">
        <mat-icon>note_add</mat-icon> {{ 'sidebar.import_menu' | translate }}
      </a>
      }
      @if (client?.sass && isActiveSaasClient && user?._role === 'CLIENT') {
      <a [href]="clientAccountUrl" class="item">
        <mat-icon>account_circle</mat-icon> {{ 'sidebar.my_account_menu' | translate }}
      </a>
      }

      <!-- ── DAILY OPERATIONS ────────────────────────────── -->
      <a [routerLink]="['/']" class="item">
        <mat-icon>dashboard</mat-icon> {{ 'sidebar.dashboard_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/analytics']" class="item">
        <mat-icon>insights</mat-icon> {{ 'sidebar.analytics_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/orders']" class="item">
        <mat-icon>shopping_cart</mat-icon> {{ 'sidebar.orders_menu' | translate }}
        @if (badges?.orders) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.orders}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/support']" class="item">
        <mat-icon>support_agent</mat-icon> {{ 'sidebar.support_menu' | translate }}
        @if (badges?.support_open) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.support_open}}</strong>
        </span>
        }
      </a>

      <!-- ── CATALOG ─────────────────────────────────────── -->
      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.catalog_menu' | translate }}</div>
      <a [routerLinkActive]="['active']" [routerLink]="['/catalog']" class="item">
        <mat-icon>shopping_bag</mat-icon> {{ 'sidebar.catalog_menu' | translate }}
        <span class="flex ml-auto">
          @if (badges?.categories) {
          <strong class="ui circular label">{{badges?.categories}}</strong>
          }
          @if (badges?.products) {
          <strong class="ui circular label ml-2">{{badges?.products}}</strong>
          }
        </span>
      </a>
      @if (hasStorefrontAccess()) {
        <a [routerLinkActive]="['active']" [routerLink]="['/storefront/homepage']" class="item">
          <mat-icon>web</mat-icon> {{ 'sidebar.homepage_menu' | translate }}
        </a>
        <a [routerLinkActive]="['active']" [routerLink]="['/storefront/checkout-recommendations']" class="item">
          <mat-icon>add_shopping_cart</mat-icon> {{ 'sidebar.checkout_recommendations_menu' | translate }}
        </a>
      }
      <a [routerLinkActive]="['active']" [routerLink]="['/banner']" class="item">
        <mat-icon>image</mat-icon> {{ 'sidebar.banners_menu' | translate }}
        @if (badges?.banners) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.banners}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/options']" class="item">
        <mat-icon>list</mat-icon> {{ 'sidebar.options_menu' | translate }}
        @if (badges?.option_lists) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.option_lists}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/discount-rule']" class="item">
        <mat-icon>local_offer</mat-icon> {{ 'sidebar.discount_rules_menu' | translate }}
        @if (badges?.discount_rules) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.discount_rules}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/voucher']" class="item">
        <mat-icon>qr_code</mat-icon> {{ 'sidebar.vouchers_menu' | translate }}
        @if (badges?.vouchers) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.vouchers}}</strong>
        </span>
        }
      </a>

      <!-- ── INVENTORY (retail / real_stock stores only) ─── -->
      @if (showInventory) {
      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.inventory_menu' | translate }}</div>
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/stock-levels']" class="item">
        <mat-icon>inventory_2</mat-icon> {{ 'sidebar.stock_levels_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/receive']" class="item">
        <mat-icon>move_to_inbox</mat-icon> {{ 'sidebar.stock_reception_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/suppliers']" class="item">
        <mat-icon>local_shipping</mat-icon> {{ 'sidebar.suppliers_menu' | translate }}
        @if (badges?.suppliers) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.suppliers}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/movements']" class="item">
        <mat-icon>history</mat-icon> {{ 'sidebar.stock_movements_menu' | translate }}
      </a>
      }

      <!-- ── KITCHEN (restaurant stores only) ────────────── -->
      @if (showKitchen) {
      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.kitchen_section' | translate }}</div>
      <!-- hide for now becouse its not ready 
        <a [routerLinkActive]="['active']" [routerLink]="['/production']" class="item">
        <mat-icon>restaurant</mat-icon> {{ 'sidebar.productions_menu' | translate }}
        @if (badges?.productions) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.productions}}</strong>
        </span>
        }
      </a> -->
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/ingredients']" class="item">
        <mat-icon>restaurant_menu</mat-icon> {{ 'sidebar.ingredients_menu' | translate }}
        @if (badges?.low_stock_ingredients) {
        <span class="flex ml-auto">
          <strong class="ui red circular label">{{badges?.low_stock_ingredients}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/menu-costs']" class="item">
        <mat-icon>price_check</mat-icon> {{ 'sidebar.menu_costs_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/stock-forecast']" class="item">
        <mat-icon>trending_down</mat-icon> {{ 'sidebar.stock_forecast_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/inventory/product-profitability']" class="item">
        <mat-icon>trending_up</mat-icon> {{ 'sidebar.product_profitability_menu' | translate }}
      </a>
      }

      <!-- ── CUSTOMERS ───────────────────────────────────── -->
      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.clients_menu' | translate }}</div>
      <a [routerLinkActive]="['active']" [routerLink]="['/clients']" class="item">
        <mat-icon>people</mat-icon> {{ 'sidebar.clients_menu' | translate }}
        @if (badges?.members) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.members}}</strong>
        </span>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/operator']" class="item">
        <mat-icon>groups</mat-icon> {{ 'sidebar.operators_menu' | translate }}
        @if (badges?.operators) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.operators}}</strong>
        </span>
        }
      </a>
      @if (showMarketing) {
      <a [routerLinkActive]="['active']" [routerLink]="['/marketing']" class="item">
        <mat-icon>send</mat-icon> {{ 'sidebar.marketing_menu' | translate }}
      </a>
      }

      <!-- ── FINANCE ─────────────────────────────────────── -->
      <!-- Product decision (PLU-192): strict separation, not a shared route
           with mixed-role auth — but that's about not mixing admin-only
           tabs/behavior into the client page, not about hiding the client
           page during impersonation. isConnectClient() (this whole block's
           guard) is true for both a genuine CLIENT session and ADMIN MODE;
           during ADMIN MODE, "connect as client" (loginApiKey) already
           populates user._token with a real, tenant-scoped Client Sanctum
           token, so this same /accounting route/component authenticates
           and scopes correctly for the impersonated merchant with zero
           admin-specific branching — exactly like every other item in this
           block (Catalog, Inventory, Kitchen, Clients, Tools) already does
           while impersonating. -->
      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.finance_section' | translate }}</div>
      <a [routerLinkActive]="['active']" [routerLink]="['/accounting']" class="item">
        <mat-icon>point_of_sale</mat-icon> {{ 'sidebar.accounting_menu' | translate }}
      </a>

      <!-- ── TOOLS ───────────────────────────────────────── -->
      <div class="divider"></div>
      <div class="item-header">{{ 'sidebar.tools_section' | translate }}</div>

      <!-- ── untill we develope and improuveTOOLS ───────────────────────────────────────── 
      <a [routerLinkActive]="['active']" [routerLink]="['/calendar']" class="item">
        <mat-icon>calendar_today</mat-icon> {{ 'sidebar.calendars_menu' | translate }}
        @if (badges?.calendars) {
        <strong class="ui circular label">{{badges?.calendars}}</strong>
        }
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/partnership']" class="item">
        <mat-icon>business</mat-icon> {{ 'sidebar.partnerships_menu' | translate }}
        @if (badges?.partnerships) {
        <span class="flex ml-auto">
          <strong class="ui circular label">{{badges?.partnerships}}</strong>
        </span>
        }
      </a> -->
      <a [routerLinkActive]="['active']" [routerLink]="['/action']" class="item">
        <mat-icon>touch_app</mat-icon> {{ 'sidebar.actions_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/activity-log']" class="item">
        <mat-icon>history</mat-icon> {{ 'sidebar.activity_log_menu' | translate }}
      </a>
      <a [routerLinkActive]="['active']" [routerLink]="['/documentation']" class="item">
        <mat-icon>description</mat-icon> {{ 'sidebar.documentations_menu' | translate }}
      </a>

      <div class="divider"></div>
      <a (click)="logout()" class="item logout-item" role="button" tabindex="0" (keydown.enter)="logout()"
        (keydown.space)="logout(); $event.preventDefault()">
        <mat-icon>exit_to_app</mat-icon> {{ 'sidebar.logout_menu' | translate }}
      </a>
    </div>
    }
  </div>

  <div class="sidebar-footer">
    <div class="footer-top-row">
      <div class="lang-selector">
        <button [class.active]="currentLang === 'en'" (click)="setLanguage('en')">EN</button>
        <span class="sep">|</span>
        <button [class.active]="currentLang === 'nl'" (click)="setLanguage('nl')">NL</button>
      </div>
      @if (user?._role === 'ADMIN') {
      <app-notification-bell></app-notification-bell>
      }
    </div>
    <div class="legal-links">
      <a routerLink="/legal" target="_blank">{{ 'sidebar.legal_mentions' | translate }}</a>
    </div>
    <div class="copyright">
      &copy; {{currentYear}} {{ selectedSite?.name ? ('sidebar.powered_by' | translate) : 'Vlugbezorgd' }}
    </div>
  </div>
</div>